import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Shield, ArrowLeft, RefreshCw, LogOut } from "lucide-react";

import {
  adminLogin,
  adminFetch,
  getAdminToken,
} from "../lib/auth";

const ADMIN_TOKEN_KEY = "cei.admin.token";

function fmtDuration(sec) {
  if (sec == null) return "—";
  const s = Number(sec) || 0;
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m < 60) return `${m}m ${r}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AdminPage() {
  const [authed, setAuthed] = useState(!!getAdminToken());
  const [password, setPassword] = useState("");
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const [summary, setSummary] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [s, se, ev] = await Promise.all([
        adminFetch("/admin/stats"),
        adminFetch("/admin/sessions"),
        adminFetch("/admin/events"),
      ]);
      setSummary(s);
      setSessions(se.sessions || []);
      setEvents(ev.events || []);
    } catch (e) {
      // token may have expired
      if (/401|403/.test(String(e))) {
        localStorage.removeItem(ADMIN_TOKEN_KEY);
        setAuthed(false);
      } else {
        setErr(String(e));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authed) {
      refresh();
      const t = setInterval(refresh, 15000);
      return () => clearInterval(t);
    }
  }, [authed, refresh]);

  async function onLogin(e) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      await adminLogin(password.trim());
      setAuthed(true);
    } catch (e2) {
      setErr(e2.message || "Login failed");
    } finally {
      setBusy(false);
    }
  }

  function onLogoutAdmin() {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    setAuthed(false);
    setSummary(null);
    setSessions([]);
    setEvents([]);
  }

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-4">
        <div
          className="w-full max-w-md bg-white border border-gray-200 shadow-sm"
          data-testid="admin-login-card"
        >
          <div className="border-b border-gray-100 px-6 py-5 flex items-center gap-3">
            <div className="h-10 w-10 border border-[#2F4F4F] bg-[#2F4F4F] text-white flex items-center justify-center flex-shrink-0">
              <Shield className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Admin access</h1>
              <p className="text-xs text-gray-600">Session audit console</p>
            </div>
          </div>
          <form onSubmit={onLogin} className="p-6 space-y-4">
            <div>
              <label
                htmlFor="adminpwd"
                className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5"
              >
                Admin password
              </label>
              <input
                id="adminpwd"
                data-testid="admin-password-input"
                type="password"
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2F4F4F]/30 focus:border-[#2F4F4F]"
              />
            </div>
            {err ? (
              <p
                className="text-xs text-rose-800 bg-rose-50 border border-rose-200 px-3 py-2"
                data-testid="admin-error"
              >
                {err}
              </p>
            ) : null}
            <button
              type="submit"
              disabled={busy || !password}
              className="w-full bg-[#2F4F4F] text-white px-4 py-2.5 text-sm font-medium hover:bg-[#1A3636] disabled:opacity-60"
              data-testid="admin-submit"
            >
              {busy ? "Verifying…" : "Enter admin console"}
            </button>
            <Link
              to="/"
              className="flex items-center justify-center gap-1 text-xs text-gray-500 hover:text-gray-800"
            >
              <ArrowLeft className="h-3 w-3" />
              Back to dashboard
            </Link>
          </form>
        </div>
      </div>
    );
  }

  const totals = summary?.totals || {};
  const perLabel = summary?.per_password || [];

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="admin-page">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 border border-[#2F4F4F] bg-[#2F4F4F] text-white flex items-center justify-center">
              <Shield className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">
                Admin · Access audit
              </h1>
              <p className="text-xs text-gray-600">
                Community Engagement Insights · session tracking
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={refresh}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 border border-gray-300 bg-white hover:bg-gray-50"
              data-testid="admin-refresh"
            >
              <RefreshCw
                className={"h-3.5 w-3.5 " + (loading ? "animate-spin" : "")}
              />
              Refresh
            </button>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 border border-gray-300 bg-white hover:bg-gray-50"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Dashboard
            </Link>
            <button
              onClick={onLogoutAdmin}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 border border-rose-200 text-rose-800 bg-rose-50 hover:bg-rose-100"
              data-testid="admin-logout"
            >
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-10">
        {/* Totals */}
        <section data-testid="admin-totals">
          <h2 className="text-xl font-semibold tracking-tight text-gray-900 mb-3">
            At a glance
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <TotalKpi label="Sessions total" value={totals.sessions ?? 0} />
            <TotalKpi label="Active now" value={totals.sessions_active ?? 0} />
            <TotalKpi label="Successful logins" value={totals.logins ?? 0} />
            <TotalKpi
              label="Failed attempts"
              value={totals.logins_failed ?? 0}
              tone="danger"
            />
            <TotalKpi
              label="Total time on site"
              value={fmtDuration(totals.total_duration_seconds ?? 0)}
            />
          </div>
        </section>

        {/* Per password */}
        <section data-testid="admin-per-password">
          <h2 className="text-xl font-semibold tracking-tight text-gray-900 mb-3">
            Usage by password
          </h2>
          <div className="bg-white border border-gray-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-3 text-left">Password label</th>
                  <th className="px-3 py-3 text-right">Sessions</th>
                  <th className="px-3 py-3 text-right">Active</th>
                  <th className="px-3 py-3 text-right">Logins</th>
                  <th className="px-3 py-3 text-right">Failed</th>
                  <th className="px-3 py-3 text-right">Avg duration</th>
                  <th className="px-3 py-3 text-right">Total time</th>
                  <th className="px-3 py-3 text-right">Unique IPs</th>
                  <th className="px-3 py-3 text-left">Last used</th>
                </tr>
              </thead>
              <tbody>
                {perLabel.map((r) => (
                  <tr
                    key={r.label}
                    className="border-b border-gray-100"
                    data-testid={`admin-row-label-${r.label}`}
                  >
                    <td className="px-3 py-3 font-medium text-gray-900">
                      {r.label}
                      {r.is_admin_key ? (
                        <span className="ml-2 text-[10px] uppercase tracking-wider text-[#2F4F4F]">
                          admin
                        </span>
                      ) : null}
                    </td>
                    <td className="px-3 py-3 text-right font-mono">
                      {r.sessions_total}
                    </td>
                    <td className="px-3 py-3 text-right font-mono">
                      {r.sessions_active > 0 ? (
                        <span className="text-emerald-700">
                          {r.sessions_active}
                        </span>
                      ) : (
                        r.sessions_active
                      )}
                    </td>
                    <td className="px-3 py-3 text-right font-mono">
                      {r.logins}
                    </td>
                    <td className="px-3 py-3 text-right font-mono">
                      {r.logins_failed > 0 ? (
                        <span className="text-rose-800">{r.logins_failed}</span>
                      ) : (
                        0
                      )}
                    </td>
                    <td className="px-3 py-3 text-right font-mono">
                      {fmtDuration(r.avg_duration_seconds)}
                    </td>
                    <td className="px-3 py-3 text-right font-mono">
                      {fmtDuration(r.total_duration_seconds)}
                    </td>
                    <td className="px-3 py-3 text-right font-mono">
                      {r.unique_ips}
                    </td>
                    <td className="px-3 py-3 text-gray-700">
                      {fmtDate(r.last_used)}
                    </td>
                  </tr>
                ))}
                {perLabel.length === 0 ? (
                  <tr>
                    <td
                      colSpan={9}
                      className="px-3 py-10 text-center text-sm text-gray-500"
                    >
                      No usage yet.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>

        {/* Sessions */}
        <section data-testid="admin-sessions">
          <h2 className="text-xl font-semibold tracking-tight text-gray-900 mb-3">
            Sessions
          </h2>
          <div className="bg-white border border-gray-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-3 text-left">Started</th>
                  <th className="px-3 py-3 text-left">Password</th>
                  <th className="px-3 py-3 text-left">State</th>
                  <th className="px-3 py-3 text-left">Duration</th>
                  <th className="px-3 py-3 text-left">IP</th>
                  <th className="px-3 py-3 text-left">Browser</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s, i) => (
                  <tr
                    key={`${s.token_preview}-${s.started_at}`}
                    className="border-b border-gray-100"
                    data-testid={`admin-session-row-${i}`}
                  >
                    <td className="px-3 py-3 text-gray-800 whitespace-nowrap">
                      {fmtDate(s.started_at)}
                    </td>
                    <td className="px-3 py-3 font-medium">
                      {s.password_label}
                      {s.is_admin ? (
                        <span className="ml-2 text-[10px] uppercase tracking-wider text-[#2F4F4F]">
                          admin
                        </span>
                      ) : null}
                    </td>
                    <td className="px-3 py-3">
                      {s.is_active ? (
                        <span className="inline-flex items-center gap-1 text-emerald-800 text-xs">
                          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                          Active
                        </span>
                      ) : s.ended_at ? (
                        <span className="text-gray-500 text-xs">Ended</span>
                      ) : (
                        <span className="text-gray-500 text-xs">Idle</span>
                      )}
                    </td>
                    <td className="px-3 py-3 font-mono text-gray-800">
                      {fmtDuration(s.duration_seconds)}
                    </td>
                    <td className="px-3 py-3 font-mono text-gray-700">
                      {s.ip || "—"}
                    </td>
                    <td
                      className="px-3 py-3 text-xs text-gray-600 max-w-xs truncate"
                      title={s.user_agent}
                    >
                      {s.user_agent || "—"}
                    </td>
                  </tr>
                ))}
                {sessions.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-3 py-10 text-center text-sm text-gray-500"
                    >
                      No sessions yet.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>

        {/* Events */}
        <section data-testid="admin-events">
          <h2 className="text-xl font-semibold tracking-tight text-gray-900 mb-3">
            Recent events
          </h2>
          <div className="bg-white border border-gray-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-3 text-left">When</th>
                  <th className="px-3 py-3 text-left">Event</th>
                  <th className="px-3 py-3 text-left">Password</th>
                  <th className="px-3 py-3 text-left">IP</th>
                  <th className="px-3 py-3 text-left">Browser</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e, i) => (
                  <tr
                    key={e.id || `${e.at}-${i}`}
                    className="border-b border-gray-100"
                    data-testid={`admin-event-row-${i}`}
                  >
                    <td className="px-3 py-3 text-gray-800 whitespace-nowrap">
                      {fmtDate(e.at)}
                    </td>
                    <td className="px-3 py-3">
                      {e.type === "login" ? (
                        <span className="text-emerald-800 text-xs">login</span>
                      ) : e.type === "login_failed" ? (
                        <span className="text-rose-800 text-xs">
                          login_failed
                        </span>
                      ) : (
                        <span className="text-gray-600 text-xs">{e.type}</span>
                      )}
                    </td>
                    <td className="px-3 py-3">{e.password_label || "—"}</td>
                    <td className="px-3 py-3 font-mono text-gray-700">
                      {e.ip || "—"}
                    </td>
                    <td
                      className="px-3 py-3 text-xs text-gray-600 max-w-xs truncate"
                      title={e.user_agent}
                    >
                      {e.user_agent || "—"}
                    </td>
                  </tr>
                ))}
                {events.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-3 py-10 text-center text-sm text-gray-500"
                    >
                      No events yet.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}

function TotalKpi({ label, value, tone }) {
  const accent = tone === "danger" ? "text-rose-800" : "text-gray-900";
  return (
    <div className="bg-white border border-gray-200 p-4">
      <div className="text-[11px] uppercase tracking-wider text-gray-500">
        {label}
      </div>
      <div className={"text-2xl font-bold font-mono mt-1 " + accent}>
        {value}
      </div>
    </div>
  );
}
